import React from 'react'
import { useEffect } from 'react'
import { Box } from '@mui/material'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { useForm, UseFormReturn } from 'react-hook-form'
import { inputProperties } from '../../../../types/inputProperties'
import { DeliveryMethodType } from '../../../../types/deliveryMethodTypes'
import ACHDeliveryMethod from './ACHDeliveryMethod'
import WireDeliveryMethod from './WireDeliveryMethod'
import CheckDeliveryMethod from './CheckDeliveryMethod'
import { TabType } from '../components/DeliveryMethodTypes'
import { DELIVERY_METHOD_TITLE } from '../../../../utils/deliveryMethods'

interface TabProps {
  id: string
  title: string
  fillerComponent: TabType
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel = (props: TabPanelProps) => (
  <div
    role='tabpanel'
    hidden={props.value !== props.index}
    id={`delivery-method-tabpanel-${props.index}`}
  >
    {props.value === props.index && <Box>{props.children}</Box>}
  </div>
)

export interface TabbedDeliveryMethodProps {
  type?: inputProperties
  accountNumber?: inputProperties
  routingNumber?: inputProperties
  beneficiaryName?: inputProperties
  country?: inputProperties
  line1?: inputProperties
  line2?: inputProperties
  city?: inputProperties
  state?: inputProperties
  postalCode?: inputProperties
  payableTo?: inputProperties
  form?: UseFormReturn
  onClickBack?: Function
  onSubmit?: Function
  titleLabel?: string
  tabs?: Array<TabProps>
}

const TabbedDeliveryMethod = (props: TabbedDeliveryMethodProps) => {
  const useFormDefault = props.form || useForm()
  const { reset } = useFormDefault

  const { ACH, CHECK, WIRE } = DeliveryMethodType
  interface categoryHierarchyType {
    [key: string]: JSX.Element
  }
  const TAB_COMPONENT_MAP: categoryHierarchyType = {
    [ACH]: (
      <ACHDeliveryMethod
        accountNumber={props.accountNumber}
        beneficiaryName={props.beneficiaryName}
        routingNumber={props.routingNumber}
        form={useFormDefault}
      />
    ),
    [CHECK]: <CheckDeliveryMethod form={useFormDefault} />,
    [WIRE]: (
      <WireDeliveryMethod
        accountNumber={props.accountNumber}
        beneficiaryName={props.beneficiaryName}
        country={props.country}
        routingNumber={props.routingNumber}
        form={useFormDefault}
      />
    ),
  }

  const [tabValue, setTabValue] = React.useState(0)
  useEffect(() => {
    const initialTab = props?.type?.value
    if (initialTab) {
      const tabIndex = Object.keys(TAB_COMPONENT_MAP).indexOf(initialTab)
      if (tabIndex >= 0) setTabValue(tabIndex)
    }
    return () => {}
  }, [])
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    //Reset the form when the user changes the tab to clean fields
    reset()
  }
  return (
    <>
      <Tabs
        sx={theme => ({
          marginBottom: theme.spacing(1.5),
          minHeight: 'initial',
          maxHeight: theme.spacing(3.125),
        })}
        value={tabValue}
        onChange={handleChange}
      >
        {props.tabs?.map((tab, index) => (
          <Tab
            sx={theme => ({
              textTransform: 'none',
              color: '#5D5D5D',
              fontWeight: '400',
              minHeight: 'initial',
              maxHeight: theme.spacing(3.125),
              fontSize: '1rem',
              marginLeft: index === 0 ? 0 : theme.spacing(1.5),
              padding: theme.spacing(0, 0, 0.5, 0),
              '&.Mui-selected': {
                color: '#1D1D1D',
                fontWeight: '600',
              },
            })}
            disableRipple
            label={tab.title}
            key={index}
            id={tab.id}
          />
        ))}
      </Tabs>
      {props.tabs?.map((tab, index) => (
        <TabPanel value={tabValue} key={index} index={index}>
          {TAB_COMPONENT_MAP[tab.fillerComponent]}
        </TabPanel>
      ))}
    </>
  )
}

TabbedDeliveryMethod.defaultProps = {
  titleLabel: 'Delivery Method',
  tabs: [
    {
      id: 'ach',
      title: DELIVERY_METHOD_TITLE.ACH,
      fillerComponent: DeliveryMethodType.ACH,
    },
    {
      id: 'check',
      title: DELIVERY_METHOD_TITLE.CHECK,
      fillerComponent: DeliveryMethodType.CHECK,
    },
    {
      id: 'wire',
      title: DELIVERY_METHOD_TITLE.WIRE,
      fillerComponent: DeliveryMethodType.WIRE,
    },
  ],
}

export default TabbedDeliveryMethod
